import React, { useState } from "react";
import { useUpdateUser, useUsers } from "../../api/users";
import { useAuth } from "../../auth/AuthContext";
import { StatusTag } from "../../components/StatusTag";
import { PaginationBar } from "../../components/PaginationBar";
import { formatDate } from "../../utils/money";
import { useToast } from "../../components/ToastProvider";
import { NewUserModal } from "./NewUserModal";
import { EditUserModal } from "./EditUserModal";
import { ResetPasswordModal } from "./ResetPasswordModal";
import { EditIconButton } from "../../components/RowIconButton";
import { StaffUserRow } from "../../types/dtos";

const ROLE_OPTIONS: Array<{ value: "ADMIN" | "FINANCEIRO" | "SUPORTE" | "LEITURA"; label: string }> = [
  { value: "ADMIN", label: "Administrador" },
  { value: "FINANCEIRO", label: "Financeiro" },
  { value: "SUPORTE", label: "Suporte" },
  { value: "LEITURA", label: "Leitura" },
];

export default function UsersPage() {
  const { user: me } = useAuth();
  const [page, setPage] = useState(1);
  const [showNew, setShowNew] = useState(false);
  const [resetTarget, setResetTarget] = useState<{ id: number; name: string } | null>(null);
  const [editingUser, setEditingUser] = useState<StaffUserRow | null>(null);
  const { data, isLoading, isError } = useUsers({ page, pageSize: 7 });
  const update = useUpdateUser();
  const toast = useToast();

  async function handleRoleChange(id: number, role: (typeof ROLE_OPTIONS)[number]["value"]) {
    try {
      await update.mutateAsync({ id, input: { role } });
      toast("Papel atualizado.");
    } catch {
      toast("Não foi possível atualizar o papel.");
    }
  }

  async function handleToggleActive(id: number, active: boolean) {
    try {
      await update.mutateAsync({ id, input: { active } });
      toast(active ? "Usuário reativado." : "Usuário desativado.");
    } catch {
      toast("Não foi possível atualizar o status.");
    }
  }

  return (
    <div className="d-flex flex-column gap-3">
      <div className="row g-2">
        <div className="col-md-9" />
        <div className="col-md-3">
          <button type="button" className="btn btn-primary w-100" onClick={() => setShowNew(true)}>
            Novo usuário
          </button>
        </div>
      </div>

      {isLoading && <div className="text-secondary">Carregando…</div>}
      {isError && <div className="text-danger">Não foi possível carregar os usuários.</div>}

      {data && (
        <div className="nt-table-wrap" style={{ overflowX: "auto" }}>
          <table className="table nt-table-fade table-striped align-middle">
            <thead>
              <tr>
                <th>Nome</th>
                <th>E-mail</th>
                <th style={{ width: 170 }}>Papel</th>
                <th style={{ width: 110 }}>Status</th>
                <th style={{ width: 140 }}>Último acesso</th>
                <th style={{ width: 230 }}></th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((u) => (
                <tr key={u.id}>
                  <td style={{ fontFamily: "var(--font-heading)" }}>{u.name}</td>
                  <td className="text-secondary">{u.email}</td>
                  <td>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 160 }}
                      value={u.role}
                      disabled={update.isPending || u.id === me?.id}
                      onChange={(e) => handleRoleChange(u.id, e.target.value as (typeof ROLE_OPTIONS)[number]["value"])}
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <StatusTag status={u.active ? "ATIVO" : "INATIVO"} />
                  </td>
                  <td className="nt-tabular text-secondary">{u.lastLoginAt ? formatDate(u.lastLoginAt) : "—"}</td>
                  <td className="text-end">
                    <div className="d-flex gap-2 justify-content-end flex-wrap">
                      <EditIconButton onClick={() => setEditingUser(u)} />
                      <button type="button" className="btn btn-ghost" style={{ fontSize: 12.5 }} onClick={() => setResetTarget({ id: u.id, name: u.name })}>
                        Redefinir senha
                      </button>
                      {u.id !== me?.id && (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          style={{ fontSize: 12.5 }}
                          disabled={update.isPending}
                          onClick={() => handleToggleActive(u.id, !u.active)}
                        >
                          {u.active ? "Desativar" : "Reativar"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {data.data.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-secondary text-center py-4">
                    Nenhum usuário cadastrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {data && <PaginationBar page={data.page} pageSize={data.pageSize} total={data.total} onPageChange={setPage} />}

      <NewUserModal show={showNew} onClose={() => setShowNew(false)} />
      <EditUserModal user={editingUser} onClose={() => setEditingUser(null)} />
      <ResetPasswordModal userId={resetTarget?.id ?? null} userName={resetTarget?.name ?? ""} onClose={() => setResetTarget(null)} />
    </div>
  );
}
